import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import Terms1OnlineService from "../../../legal/Terms1OnlineService";
import Terms2ServiceOnDemand from "../../../legal/Terms2ServiceOnDemand";
import Terms3GDPR from "../../../legal/Terms3GDPR";
import SearchableCountryDropdown from "../components/SearchableCountryDropdown";
import { calculatePricing, formatCurrency, pricingConfig } from "../utils/pricingUtils";

const captchaUrl = process.env.REACT_APP_CAPTCHA_URL || 'https://cashmanager.lv:8445'; // 'https://dzerbene.nonagon.lv:8445';

// Input sanitization utility
const sanitizeInput = (input, type = 'text') => {
  if (typeof input !== 'string') return '';
  
  let sanitized = input;
  
  switch (type) {
    case 'email':
      sanitized = sanitized.replace(/[^a-zA-Z0-9@._-]/g, '');
      break;
    case 'name':
      sanitized = sanitized.replace(/[^a-zA-Z\s'-]/g, '');
      break;
    case 'company':
      sanitized = sanitized.replace(/[^a-zA-Z0-9\s&.,'-]/g, '');
      break;
    case 'country':
      sanitized = sanitized.replace(/[^a-zA-Z\s'-]/g, '');
      break;
    default:
      sanitized = sanitized
        .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
        .replace(/<[^>]*>/g, '')
        .replace(/javascript:/gi, '')
        .replace(/on\w+\s*=/gi, '');
      break;
  }
  
  return sanitized;
};

// HTML encoding utility
const htmlEncode = (input) => {
  if (typeof input !== 'string') return '';
  
  return input
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#x27;');
};

// Input validation utility
const validateInput = (input, type = 'text') => {
  if (!input || input.trim().length === 0) return { isValid: false, error: 'This field is required' };
  
  const trimmed = input.trim();
  
  switch (type) {
    case 'email': {
      const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
      if (!emailRegex.test(trimmed)) {
        return { isValid: false, error: 'Please enter a valid email address' };
      }
      if (trimmed.length > 254) {
        return { isValid: false, error: 'Email address is too long' };
      }
      break;
    }
    case 'company': {
      if (trimmed.length < 2) {
        return { isValid: false, error: 'Company name must be at least 2 characters long' };
      }
      if (trimmed.length > 200) {
        return { isValid: false, error: 'Company name is too long' };
      }
      break;
    }
    case 'country': {
      if (trimmed.length < 2) {
        return { isValid: false, error: 'Country name must be at least 2 characters long' };
      }
      if (trimmed.length > 100) {
        return { isValid: false, error: 'Country name is too long' };
      }
      break;
    }
  }
  
  return { isValid: true, error: '' };
};

const SubmissionPage = ({ data, onUpdate }) => {
  const [email, setEmail] = useState(data.email || '');
  const [companyName, setCompanyName] = useState(data.companyName || '');
  const [country, setCountry] = useState(data.country || '');
  const [allTermsAccepted, setAllTermsAccepted] = useState(data.allTermsAccepted || false);
  
  const [showTerms1, setShowTerms1] = useState(false);
  const [showTerms2, setShowTerms2] = useState(false);
  const [showTerms3, setShowTerms3] = useState(false);
  
  // Captcha state
  const [showCaptcha, setShowCaptcha] = useState(false);
  const [currentSessionId, setCurrentSessionId] = useState(null);
  const [clickedAreas, setClickedAreas] = useState(new Set());
  const [allAreas, setAllAreas] = useState([]);
  const [instructionText, setInstructionText] = useState('');
  const [tickboxes, setTickboxes] = useState(new Map());
  const [isCaptchaValidated, setIsCaptchaValidated] = useState(false);
  const [captchaImage, setCaptchaImage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [statusMessage, setStatusMessage] = useState({ text: '', type: '' });
  const [isSubmissionSuccessful, setIsSubmissionSuccessful] = useState(false);
  const [hasCaptchaError, setHasCaptchaError] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({
    email: '',
    companyName: '',
    country: '',
    terms: ''
  });

  // Ref for captcha section to scroll into view
  const captchaSectionRef = useRef(null);

  // Update parent component when data changes
  useEffect(() => {
    onUpdate({
      email,
      companyName,
      country,
      allTermsAccepted,
      // Keep old fields for backward compatibility
      termsAccepted: allTermsAccepted,
      serviceTermsAccepted: allTermsAccepted,
      gdprAccepted: allTermsAccepted
    });
  }, [email, companyName, country, allTermsAccepted]);

  // Scroll captcha into view when it's loaded and displayed
  useEffect(() => {
    if (captchaImage && !isLoading && !isSending && captchaSectionRef.current) {
      captchaSectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [captchaImage, isLoading, isSending]);

  const handleEmailChange = (e) => {
    const value = e.target.value;
    const sanitizedValue = sanitizeInput(value, 'email');
    setEmail(sanitizedValue);
    
    const validation = validateInput(sanitizedValue, 'email');
    setFieldErrors(prev => ({
      ...prev,
      email: validation.isValid ? '' : validation.error
    }));
  };

  const handleCompanyNameChange = (e) => {
    const value = e.target.value;
    const sanitizedValue = sanitizeInput(value, 'company');
    setCompanyName(sanitizedValue);
    
    const validation = validateInput(sanitizedValue, 'company');
    setFieldErrors(prev => ({
      ...prev,
      companyName: validation.isValid ? '' : validation.error
    }));
  };

  const handleCountryChange = (e) => {
    const value = e.target.value;
    // Country is selected from dropdown, so no need to sanitize
    setCountry(value);
    
    const validation = validateInput(value, 'country');
    setFieldErrors(prev => ({
      ...prev,
      country: validation.isValid ? '' : validation.error
    }));
  };

  // Check if form is valid
  const isFormValid = () => {
    const emailValidation = validateInput(email, 'email');
    const companyValidation = validateInput(companyName, 'company');
    const countryValidation = validateInput(country, 'country');
    
    return emailValidation.isValid && 
           companyValidation.isValid && 
           countryValidation.isValid && 
           allTermsAccepted;
  };

  const showCaptchaSection = () => {
    // Validate all fields
    let hasErrors = false;
    const newFieldErrors = {};
    
    const emailValidation = validateInput(email, 'email');
    const companyValidation = validateInput(companyName, 'company');
    const countryValidation = validateInput(country, 'country');
    
    if (!emailValidation.isValid) {
      newFieldErrors.email = emailValidation.error;
      hasErrors = true;
    }
    if (!companyValidation.isValid) {
      newFieldErrors.companyName = companyValidation.error;
      hasErrors = true;
    }
    if (!countryValidation.isValid) {
      newFieldErrors.country = countryValidation.error;
      hasErrors = true;
    }
    if (!allTermsAccepted) {
      newFieldErrors.terms = 'Please accept all terms and conditions';
      hasErrors = true;
    }
    
    setFieldErrors(prev => ({
      ...prev,
      ...newFieldErrors
    }));
    
    if (hasErrors) {
      showStatus('Please fix the errors above before proceeding', 'error');
      return;
    }

    setShowCaptcha(true);
    generateCaptcha();
  };

  const generateCaptcha = async () => {
    try {
      // Clear any previous error messages and error state
      setStatusMessage({ text: '', type: '' });
      setHasCaptchaError(false);
      setClickedAreas(new Set());
      setTickboxes(new Map());
      setIsCaptchaValidated(false);
      setIsLoading(true);
      
      const response = await fetch(`${captchaUrl}/api/captcha/generate-v2`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
      });
      
      const responseData = await response.json();
      
      if (responseData.success) {
        setCurrentSessionId(responseData.sessionId);
        setCaptchaImage(responseData.imageData);
        setAllAreas(responseData.allAreas || []);
        setInstructionText(responseData.instructionText || '');
        
        setStatusMessage({ text: '', type: '' });
      } else {
        showStatus('Failed to generate challenge: ' + responseData.error, 'error');
      }
    } catch (error) {
      showStatus('Error generating challenge: ' + error.message, 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleImageClick = (event) => {
    if (isCaptchaValidated) {
      showStatus('Challenge already validated. Generate a new one to continue.', 'error');
      return;
    }

    if (hasCaptchaError) {
      showStatus('Please generate a new challenge first.', 'error');
      return;
    }

    const rect = event.target.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;

    const scaleX = event.target.naturalWidth / event.target.offsetWidth;
    const scaleY = event.target.naturalHeight / event.target.offsetHeight;

    const scaledX = x * scaleX;
    const scaledY = y * scaleY;

    const clickedArea = findClickedArea(scaledX, scaledY);

    if (clickedArea) {
      const areaKey = `${clickedArea.x}-${clickedArea.y}`;
      const newClickedAreas = new Set(clickedAreas);

      if (newClickedAreas.has(areaKey)) {
        newClickedAreas.delete(areaKey);
        hideTickbox(areaKey);
      } else {
        newClickedAreas.add(areaKey);
        showTickbox(clickedArea);
      }

      setClickedAreas(newClickedAreas);
    } else {
      showStatus('Please click on one of the images', 'error');
    }
  };

  const findClickedArea = (x, y) => {
    return allAreas.find(area => 
      x >= area.x - area.width / 2 && x <= area.x + area.width / 2 &&
      y >= area.y - area.height / 2 && y <= area.y + area.height / 2
    );
  };

  const showTickbox = (area) => {
    const areaKey = `${area.x}-${area.y}`;
    
    hideTickbox(areaKey);
    
    const tickbox = document.createElement('div');
    tickbox.className = 'tickbox';
    tickbox.textContent = '✓';
    
    const image = document.getElementById('captchaImage');
    
    if (image.naturalWidth === 0 || image.naturalHeight === 0) {
      image.onload = () => {
        positionTickbox(tickbox, area, image);
      };
      return;
    }
    
    positionTickbox(tickbox, area, image);
  };
  
  const positionTickbox = (tickbox, area, image) => {
    const scaleX = image.offsetWidth / image.naturalWidth;
    const scaleY = image.offsetHeight / image.naturalHeight;
    
    const iconX = (area.x - area.width / 2) * scaleX;
    const iconY = (area.y - area.height / 2) * scaleY;
    const iconWidth = area.width * scaleX;
    
    const imageRect = image.getBoundingClientRect();
    const containerRect = image.parentElement.getBoundingClientRect();
    
    const imageOffsetX = imageRect.left - containerRect.left;
    const imageOffsetY = imageRect.top - containerRect.top;
    
    tickbox.style.position = 'absolute';
    tickbox.style.left = (imageOffsetX + iconX + iconWidth - 25) + 'px';
    tickbox.style.top = (imageOffsetY + iconY + 5) + 'px';
    
    const imageContainer = document.getElementById('imageContainer');
    imageContainer.appendChild(tickbox);
    
    const areaKey = `${area.x}-${area.y}`;
    const newTickboxes = new Map(tickboxes);
    newTickboxes.set(areaKey, tickbox);
    setTickboxes(newTickboxes);
  };

  const hideTickbox = (areaKey) => {
    const tickbox = tickboxes.get(areaKey);
    if (tickbox) {
      tickbox.remove();
      const newTickboxes = new Map(tickboxes);
      newTickboxes.delete(areaKey);
      setTickboxes(newTickboxes);
    }
  };

//   const clearAllTickboxes = () => {
//     tickboxes.forEach(tickbox => tickbox.remove());
//     setTickboxes(new Map());
//   };

  // Format wizard data for email message
  const formatSubscriptionRequest = () => {
    const companyCount = data.selectedOption && ['1', '2', '3', '4', '5'].includes(data.selectedOption)
      ? data.selectedOption
      : data.companyCount || '1';

    // Calculate pricing using the shared utility
    const billingPeriod = data.billingPeriod || 'yearly';
    const pricing = calculatePricing(data, billingPeriod);

    let message = 'CashManager subscription request:\n\n';
    message += `Email: ${htmlEncode(email)}\n`;
    message += `Company: ${htmlEncode(companyName)}\n`;
    message += `Country: ${htmlEncode(country)}\n\n`;
    message += `Configuration:\n`;
    message += `- Usage Type: ${data.usageType === 'standalone' ? 'Standalone system' : 'With existing system'}\n`;
    if (data.usageType === 'with-existing' && data.existingSystem) {
      message += `- Existing System: ${htmlEncode(data.existingSystem)}\n`;
    }
    message += `- Number of Companies: ${companyCount}\n`;
    message += `- Billing Period: ${pricingConfig.billingPeriodLabels[billingPeriod] || billingPeriod}\n\n`;
    
    if (data.selectedFeatures && data.selectedFeatures.length > 0) {
      message += `Selected Features:\n`;
      data.selectedFeatures.forEach(feature => {
        message += `- ${pricingConfig.featureLabels[feature] || feature}\n`;
      });
      message += '\n';
    }
    
    if (data.customerInvoiceCount) {
      message += `Customer Invoice Volume: ${htmlEncode(data.customerInvoiceCount)}\n`;
    }
    if (data.supplierInvoiceCount) {
      message += `Supplier Invoice Volume: ${htmlEncode(data.supplierInvoiceCount)}\n`;
    }
    if (data.supportType && data.usageType === 'standalone') {
      message += `Support Type: ${data.supportType === 'premium-support' ? 'Premium support' : 'Self-service support'}\n`;
    }
    
    // Add pricing information
    message += `\nPricing:\n`;
    message += `- Base Plan (${pricingConfig.billingPeriodLabels[billingPeriod].toLowerCase()}): ${formatCurrency(pricing.basePrice)}\n`;
    
    const renderFeatureLine = (feature) => {
      if (!(feature.price > 0 || feature.isIncluded)) {
        return '';
      }

      let featureLine = `- ${pricingConfig.featureLabels[feature.feature]}`;
      if (feature.volumeBased && feature.volumeCaption) {
        if (feature.feature === 'digital-signing-smart-id' || feature.feature === 'digital-signing-id-card') {
          featureLine += ` (${feature.volumeCaption} signatures/month)`;
        } else {
          featureLine += ` (up to ${feature.volumeCaption} invoices)`;
        }
      }
      if (feature.isIncluded) {
        featureLine += ' (included)';
      }
      if (feature.price > 0) {
        featureLine += `: ${formatCurrency(feature.price)}`;
      }

      return featureLine + '\n';
    };

    if (pricing.featuresBreakdown.length > 0) {
      // Match PricingCalculationPage order:
      // non-signing features -> company subtotal/discount -> signing features
      pricing.featuresBreakdown
        .filter(feature => !['digital-signing-id-card', 'digital-signing-smart-id'].includes(feature.feature))
        .forEach(feature => {
          message += renderFeatureLine(feature);
        });
    }
    
    if (pricing.companyCount > 1) {
      message += `- Subtotal per company: ${formatCurrency(pricing.perCompanySubtotal)}\n`;
      message += `- Total for ${pricing.companyCount} companies: ${formatCurrency(pricing.perCompanySubtotal * pricing.companyCount)}\n`;
      if (pricing.bulkDiscountPercentage > 0) {
        message += `- Bulk discount (${pricing.bulkDiscountPercentage}%): -${formatCurrency(pricing.bulkDiscountAmount)}\n`;
      }
    }

    if (pricing.featuresBreakdown.length > 0) {
      pricing.featuresBreakdown
        .filter(feature => ['digital-signing-id-card', 'digital-signing-smart-id'].includes(feature.feature))
        .forEach(feature => {
          message += renderFeatureLine(feature);
        });
    }
    
    message += `\nTotal Price (${pricingConfig.billingPeriodLabels[billingPeriod].toLowerCase()}): ${formatCurrency(pricing.totalPrice)}\n`;
    
    return message;
  };

  const sendSubscriptionRequest = async () => {
    if (!currentSessionId) {
      showStatus('Please generate a captcha first', 'error');
      return;
    }

    if (clickedAreas.size === 0) {
      showStatus('Please select some images first', 'error');
      return;
    }

    if (isCaptchaValidated) {
      showStatus('Challenge already validated. Generate a new one to continue.', 'error');
      return;
    }

    const clicks = Array.from(clickedAreas).map(areaKey => {
      const [x, y] = areaKey.split('-').map(Number);
      return { x, y };
    });

    try {
      setIsSending(true);

      const subscriptionMessage = formatSubscriptionRequest();

      const response = await fetch(`${captchaUrl}/api/captcha/send-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          sessionId: currentSessionId,
          clicks: clicks,
          emailData: {
            senderName: htmlEncode(companyName),
            senderEmail: htmlEncode(email),
            message: subscriptionMessage
          }
        })
      });

      const responseData = await response.json();

      if (responseData.success) {
        if (responseData.valid) {
          showStatus('✅ Subscription request sent successfully! We\'ll be in touch soon.', 'success');
          setIsCaptchaValidated(true);
          setIsSubmissionSuccessful(true);
          // Clear captcha state to hide buttons
          setShowCaptcha(false);
        } else {
          showStatus('❌ Challenge validation failed. Please try again.', 'error');
          // Don't set isCaptchaValidated to true on error - allow retry
          setIsCaptchaValidated(false);
          setHasCaptchaError(true);
        }
      } else {
        showStatus('Error: ' + responseData.error, 'error');
        // Don't set isCaptchaValidated to true on error - allow retry
        setIsCaptchaValidated(false);
        setHasCaptchaError(true);
      }
    } catch (error) {
      showStatus('Error sending subscription request: ' + error.message, 'error');
      // Don't set isCaptchaValidated to true on error - allow retry
      setIsCaptchaValidated(false);
      setHasCaptchaError(true);
    } finally {
      setIsSending(false);
    }
  };

  const showStatus = (message, type) => {
    setStatusMessage({ text: message, type });
    
    // Don't auto-clear success messages - they should persist after successful submission
    // Only clear non-success messages after a delay
    if (type !== 'success') {
      setTimeout(() => {
        setStatusMessage(prev => {
          // Only clear if it's still the same message (not overwritten)
          if (prev.text === message) {
            return { text: '', type: '' };
          }
          return prev;
        });
      }, 5000);
    }
  };

  return (
    <div className="wizard-page submission-page">
      {!isSubmissionSuccessful ? (
        <>
          <div className="page-header">
            <h2>Complete your subscription request</h2>
            <p>Please provide your contact information and accept the terms and conditions to proceed.</p>
          </div>

          <div className="form-content">
            <div className="form-group">
              <label htmlFor="email">Email address *</label>
              <input
                type="email"
                id="email"
                name="email"
                value={email}
                onChange={handleEmailChange}
                placeholder="Enter your email address"
                className={`form-input ${fieldErrors.email ? 'error' : ''}`}
                required
              />
              {fieldErrors.email && (
                <span className="field-error">{fieldErrors.email}</span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="companyName">Company name *</label>
              <input
                type="text"
                id="companyName"
                name="companyName"
                value={companyName}
                onChange={handleCompanyNameChange}
                placeholder="Enter your company name"
                className={`form-input ${fieldErrors.companyName ? 'error' : ''}`}
                required
              />
              {fieldErrors.companyName && (
                <span className="field-error">{fieldErrors.companyName}</span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="country">Country of company registration *</label>
              <SearchableCountryDropdown
                value={country}
                onChange={handleCountryChange}
                placeholder="Select or search for a country"
                error={!!fieldErrors.country}
              />
              {fieldErrors.country && (
                <span className="field-error">{fieldErrors.country}</span>
              )}
            </div>

            <div className="form-group">
              <div className="terms-checkbox-item">
                <input
                  type="checkbox"
                  id="allTermsAccepted"
                  checked={allTermsAccepted}
                  onChange={(e) => {
                    setAllTermsAccepted(e.target.checked);
                    setFieldErrors(prev => ({
                      ...prev,
                      terms: ''
                    }));
                  }}
                  className={fieldErrors.terms ? 'error' : ''}
                />
                <label htmlFor="allTermsAccepted">
                  I agree to the{' '}
                  <a
                    href="#"
                    className="terms-link"
                    onClick={(e) => {
                      e.preventDefault();
                      setShowTerms1(true);
                    }}
                  >
                    Terms and Conditions
                  </a>
                  {' '}, the{' '}
                  <a
                    href="#"
                    className="terms-link"
                    onClick={(e) => {
                      e.preventDefault();
                      setShowTerms2(true);
                    }}
                  >
                    Terms of Service on Demand
                  </a>
                  {' '}and the{' '}
                  <a
                    href="#"
                    className="terms-link"
                    onClick={(e) => {
                      e.preventDefault();
                      setShowTerms3(true);
                    }}
                  >
                    Personal Data Processing Terms
                  </a>
                </label>
              </div>
              {fieldErrors.terms && (
                <span className="field-error">{fieldErrors.terms}</span>
              )}
            </div>

            {!showCaptcha && (
              <button 
                type="button" 
                className="btn btn-primary"
                onClick={showCaptchaSection}
                disabled={isLoading || !isFormValid()}
              >
                {isLoading ? 'Loading...' : 'Verify and submit'}
              </button>
            )}

            {showCaptcha && (
              <div className="captcha-section" ref={captchaSectionRef}>
                <p>Please complete the challenge to verify you're human:</p>
                
                {captchaImage && !isLoading && !isSending && (
                  <div className="instructions">
                    <p>{instructionText}</p>
                  </div>
                )}

                <div className="captcha-container">
                  {isLoading && (
                    <div className="spinner-container">
                      <div className="spinner"></div>
                      <p>Generating challenge...</p>
                    </div>
                  )}
                  
                  {isSending && (
                    <div className="spinner-container">
                      <div className="spinner"></div>
                      <p>Sending subscription request...</p>
                    </div>
                  )}
                  
                  {captchaImage && !isLoading && !isSending && (
                    <div className="image-container" id="imageContainer">
                      <img 
                        id="captchaImage"
                        src={captchaImage} 
                        alt="Captcha Image"
                        className="captcha-image"
                        onClick={handleImageClick}
                      />
                    </div>
                  )}
                </div>

                {captchaImage && !isLoading && !isSending && (
                  <div className="clicked-areas">
                    {clickedAreas.size === 0 ? 'No images selected' : `${clickedAreas.size} image(s) selected`}
                  </div>
                )}

                {!isCaptchaValidated && (
                  <div className="captcha-buttons">
                    <button 
                      type="button" 
                      className="btn btn-secondary"
                      onClick={generateCaptcha}
                      disabled={isLoading}
                    >
                      {isLoading ? 'Generating...' : 'Generate new challenge'}
                    </button>
                    <button 
                      type="button" 
                      className="btn btn-primary"
                      onClick={sendSubscriptionRequest}
                      disabled={clickedAreas.size === 0 || isLoading || isSending}
                    >
                      {isSending ? 'Sending...' : 'Validate & submit'}
                    </button>
                  </div>
                )}
              </div>
            )}

            {statusMessage.text && (
              <div className={`status ${statusMessage.type}`}>
                {statusMessage.text}
              </div>
            )}
          </div>
        </>
      ) : (
        <div className="form-content">
          {statusMessage.text && (
            <div className={`status ${statusMessage.type}`}>
              {statusMessage.text}
            </div>
          )}
        </div>
      )}

      {/* Legal Terms Modals */}
      <Terms1OnlineService show={showTerms1} onClick={() => setShowTerms1(false)} />
      <Terms2ServiceOnDemand show={showTerms2} onClick={() => setShowTerms2(false)} />
      <Terms3GDPR show={showTerms3} onClick={() => setShowTerms3(false)} />
    </div>
  );
};

SubmissionPage.propTypes = {
  data: PropTypes.shape({
    email: PropTypes.string,
    companyName: PropTypes.string,
    country: PropTypes.string,
    allTermsAccepted: PropTypes.bool,
    // Keep old fields for backward compatibility
    termsAccepted: PropTypes.bool,
    serviceTermsAccepted: PropTypes.bool,
    gdprAccepted: PropTypes.bool,
    selectedOption: PropTypes.string,
    companyCount: PropTypes.string,
    usageType: PropTypes.string,
    existingSystem: PropTypes.string,
    selectedFeatures: PropTypes.array,
    billingPeriod: PropTypes.string,
    customerInvoiceCount: PropTypes.string,
    supplierInvoiceCount: PropTypes.string,
    supportType: PropTypes.string
  }).isRequired,
  onUpdate: PropTypes.func.isRequired
};

export default SubmissionPage;

